import { adminApi } from './api';
import { toast } from '../components/Toast';

// "הוסף כשם חלופי" on the recipient warning chip, shared by the upload and
// document screens. The name the AI read becomes an alternative name of the
// given restaurant; the server then re-checks that restaurant's flagged docs.
// Confirmed first: the read name can be junk ("לקוח כללי סניף 1") or the doc
// can genuinely belong elsewhere, and a wrong alias silences future warnings.
// Hebrew count: "מסמך אחד" / "3 מסמכים".
export const docsCount = (n) => (n === 1 ? 'מסמך אחד' : `${n} מסמכים`);

// `docBusinessId` is the ח.פ. read from the document: the server refuses the
// alias when it contradicts the restaurant's, and reports whether the document
// now matches — onAdded runs only then, so a warning is never cleared falsely.
export function confirmAddRecipientAlias({ askConfirm, restaurantId, name, docBusinessId = null, onAdded }) {
  askConfirm({
    title: 'הוספת שם חלופי',
    message: `להוסיף את "${name}" כשם חלופי של המסעדה?`,
    detail: 'מסמכים עם השם הזה לא יסומנו יותר כלא תואמים. אם המסמך באמת שייך למסעדה אחרת — אין לאשר.',
    confirmLabel: 'הוסף שם חלופי',
    onConfirm: async () => {
      try {
        const { data } = await adminApi.addRestaurantAlias(restaurantId, name, docBusinessId);
        if (data?.matches) {
          onAdded?.();
          toast(data.recomputed ? `השם נוסף · ${docsCount(data.recomputed)} עודכנו` : 'השם נוסף');
        } else {
          toast('השם נשמר, אבל המסמך הזה עדיין לא תואם למסעדה — כדאי לבדוק אותו', 'error');
        }
      } catch (err) {
        toast(err.response?.data?.error || 'שגיאה בהוספת השם', 'error');
      }
    },
  });
}

// "זה הח.פ. של הספק" on the same chip, when the ח.פ. comparison decided the
// mismatch: the AI read the supplier's own ח.פ. as the recipient's. The number
// joins the restaurant's ignore list, so its documents are checked by name.
// onResult gets the document's new state ({ match, decided_by }) — when the
// name doesn't match either, the chip turns into "הוסף כשם חלופי".
// `docSaved`: the document is already in the DB (document screen), so the
// server's re-check counted it among `recomputed`; on the upload screen it isn't.
export function confirmIgnoreRecipientBid({ askConfirm, restaurantId, bid, name = '', docSaved = false, onResult }) {
  askConfirm({
    title: 'ח.פ. של הספק',
    message: `הח.פ. ${bid} הוא של הספק ולא של המסעדה?`,
    detail: 'מעכשיו, כשהמספר הזה ייקרא כח.פ. הנמען במסמכים של המסעדה, הבדיקה תיעשה לפי שם הנמען. אם המסמך באמת שייך לחברה אחרת — אין לאשר.',
    confirmLabel: 'כן, של הספק',
    onConfirm: () => send(false),
  });

  async function send(confirmOwner) {
    try {
      const { data } = await adminApi.ignoreRecipientBid(restaurantId, bid, name, confirmOwner);
      onResult?.({ match: data.match, decided_by: data.decided_by });
      if (data.match === 'match') {
        const others = Math.max(0, (data.recomputed || 0) - (docSaved ? 1 : 0));
        toast(others ? `המסמך תואם · ${others === 1 ? 'עוד מסמך אחד עודכן' : `עוד ${others} מסמכים עודכנו`}` : 'המסמך תואם');
      } else {
        toast('נשמר. שם הנמען במסמך לא תואם למסעדה — אפשר להוסיף אותו כשם חלופי', 'error');
      }
    } catch (err) {
      const d = err.response?.data;
      // The ח.פ. is another restaurant's in the system — a second, explicit
      // question, since that restaurant may simply be a supplier of this one.
      if (err.response?.status === 409 && d?.needs_confirm && !confirmOwner) {
        askConfirm({
          title: 'ח.פ. של מסעדה במערכת',
          message: `הח.פ. ${bid} שייך למסעדה "${d.owner}" במערכת. היא גם ספק של המסעדה הזו?`,
          detail: `אם כן — הבדיקה תעבור לשם הנמען, ומסמך שבאמת שייך ל"${d.owner}" עדיין יסומן לפי השם. אם המסמך שלה והועלה כאן בטעות — אין לאשר.`,
          confirmLabel: 'כן, היא גם ספק',
          onConfirm: () => send(true),
        });
        return;
      }
      toast(d?.error || 'שגיאה בשמירה', 'error');
    }
  }
}
